import React, { StrictMode } from "react";
import { createRoot, Root } from "react-dom/client";
import SearchApp from "../search/app";
import * as Sentry from "@sentry/browser";
import { ErrorBoundary } from "@sentry/react";
import AppProviders from "../search/providers/app_providers";
import { identify } from "../search/analytics/identification";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Global Dewey Instance
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

// Expose global Dewey.identify method immediately
declare global {
  interface Window {
    Dewey: {
      identify: typeof identify;
      identifyUser: (id: string, metadata: Record<string, string | number | boolean>) => void;
    };
  }
}

window.Dewey ||= {
  identify: identify,
  identifyUser(id: string, metadata: Record<string, string | number | boolean>) {
    const email = metadata.email;
    delete metadata.email;
    identify(id, email, metadata);
  },
};
if (!window.Dewey.identify) {
  window.Dewey.identify = identify;
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Sentry Initialization
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

const SENTRY_TAG_SOURCE = "source";
const SENTRY_TAG_WIDGET_VERSION = "search-widget-version";
const SENTRY_TAG_ERROR_BOUNDARY = "error-boundary";
const SENTRY_CONTEXT_INITIALIZATION = "search-widget-initialization";
const SENTRY_CONTEXT_ENVIRONMENT = "search-widget-environment";

const DEWEY_SENTRY_SOURCE = "dewey-search-widget";
const DEWEY_SEARCH_WIDGET_VERSION = "1.0.0";

// Extract the allowed URLs from the environment variable
const allowedUrls: string[] = import.meta.env.SENTRY_ALLOWED_URLS?.toString()?.split(",") ?? [];
Sentry.init({
  dsn: import.meta.env.SENTRY_JS_DSN?.toString(),
  release: import.meta.env.HEROKU_RELEASE_VERSION?.toString(),
  environment: import.meta.env.DEV ? "development" : "production",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: true,
      networkDetailAllowUrls: [window.location.origin, ...allowedUrls],
    }),
  ],
  beforeSend(event) {
    // Only send events that are tagged as coming from our widget
    if (event.tags?.[SENTRY_TAG_SOURCE] === DEWEY_SENTRY_SOURCE) {
      return event;
    }
    return null;
  },
  sendDefaultPii: true,
  sampleRate: 1.0,
  tracesSampleRate: 0.05,
  tracePropagationTargets: ["localhost:3000", "https://app.askdewey.co", "https://app.askdewey.com"],
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
});

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// SDK Initialization
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

let initialized = false;

function setup(apiToken: string, rootUrl: string, triggerId?: string) {
  if (!initialized) {
    defineDeweySearch(apiToken, rootUrl, triggerId);
    injectDeweySearch();
    initialized = true;
  }
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Automatic Script Loading
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

/*
Try to automatically load the search component when the script tag is added to the page
- Expecting the embedded script to look like:
  <script async type="text/javascript" src="CDN_URL_TO_SCRIPT" data-publishable-key="PUBLISHABLE_KEY"></script>
- Note that this does not work in development, because the script tag
  uses type="module", and the currentScript property is not available
*/

// Function to find the script tag and extract configuration
type SearchScriptConfiguration = {
  publishableKey?: string;
  rootUrl?: string;
  triggerId?: string;
};

function getScriptConfiguration(): SearchScriptConfiguration | undefined {
  const scriptTag = document.currentScript ?? document.getElementById("dewey--search-script");

  let publishableKey: string | undefined;
  let rootUrl: string | undefined;
  let triggerId: string | undefined;

  if (scriptTag) {
    publishableKey = scriptTag.dataset.publishableKey;
    triggerId = scriptTag.dataset.triggerId;

    if (scriptTag instanceof HTMLScriptElement) {
      rootUrl = new URL(scriptTag.src).origin;
    } else {
      rootUrl = scriptTag.dataset.rootUrl;
    }
  } else {
    return undefined;
  }

  return { publishableKey, rootUrl, triggerId };
}

// Function to initialize with configuration
function initializeSetup() {
  Sentry.withScope((scope) => {
    scope.setTag(SENTRY_TAG_SOURCE, DEWEY_SENTRY_SOURCE);
    scope.setTag(SENTRY_TAG_WIDGET_VERSION, DEWEY_SEARCH_WIDGET_VERSION);
    scope.setContext(SENTRY_CONTEXT_INITIALIZATION, {
      hostUrl: window.location.href,
      readyState: document.readyState,
    });

    const config = getScriptConfiguration();
    if (!config) {
      console.error("Dewey Search: Unable to locate the script tag, make sure it has the ID `dewey--search-script`");
      return;
    }

    const { publishableKey, rootUrl, triggerId } = config;

    if (!rootUrl) {
      // We need a root URL to load the search component, and know where to make API calls
      console.error(
        "Dewey Search: Unable to load the root URL, you may need to set it on the script tag via the `data-root-url` attribute",
      );
    } else if (!publishableKey) {
      // We need a publishable key to load the search component, and authorize API calls
      console.error(
        "Dewey Search: Unable to find your API token, it must be set on the script tag via the `data-publishable-key` attribute",
      );
    } else {
      // Setup the search component
      setup(publishableKey, rootUrl, triggerId);
    }
  });
}

// Wait for DOM to be ready before initializing
function initializeWhenDOMReady() {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeSetup);
  } else {
    initializeSetup();
  }
}
initializeWhenDOMReady();

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Custom Element Management
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

// Add our custom element to the DOM
function injectDeweySearch() {
  const rootElement = document.createElement("dewey-search");
  document.body.appendChild(rootElement);
}

// Configure our custom element, that wraps the SearchApp
function defineDeweySearch(apiToken: string, rootURL: string, triggerId?: string) {
  customElements.define(
    "dewey-search",
    class extends HTMLElement {
      private root: Root | null = null;

      constructor() {
        super();

        // Create a shadow root for our custom element
        this.attachShadow({ mode: "open" });
      }

      connectedCallback() {
        // Render the SearchApp with widget-scoped error tracking
        Sentry.withScope((scope) => {
          scope.setTag(SENTRY_TAG_SOURCE, DEWEY_SENTRY_SOURCE);
          scope.setTag(SENTRY_TAG_WIDGET_VERSION, DEWEY_SEARCH_WIDGET_VERSION);
          scope.setContext(SENTRY_CONTEXT_ENVIRONMENT, {
            hostUrl: window.location.href,
            shadowDOM: !!this.shadowRoot,
          });

          if (this.shadowRoot) {
            // Create the React root now that we're connected to the DOM
            this.root = createRoot(this.shadowRoot);

            // Render the SearchApp with widget-scoped error tracking
            this.root.render(
              <StrictMode>
                <ErrorBoundary
                  beforeCapture={(scope) => {
                    scope.setTag(SENTRY_TAG_SOURCE, DEWEY_SENTRY_SOURCE);
                    scope.setTag(SENTRY_TAG_ERROR_BOUNDARY, "widget-root");
                  }}
                  showDialog={false}
                >
                  <AppProviders
                    apiToken={apiToken}
                    rootURL={rootURL}
                    shadowRoot={this.shadowRoot}
                    triggerId={triggerId}
                  >
                    <SearchApp />
                  </AppProviders>
                </ErrorBoundary>
              </StrictMode>,
            );
          } else {
            throw new Error("Shadow DOM is not supported");
          }
        });
      }

      disconnectedCallback() {
        this.root?.unmount();
        this.root = null;
      }
    },
  );
}
